import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import ApplicationTable from "@/components/applications/application-table";
import AddApplicationDialog from "@/components/applications/add-application-dialog";
import ApplicationFilterDialog from "@/components/applications/application-filter-dialog";
import { DeleteConfirmationDialog } from "@/components/applications/delete-confirmation-dialog";
import { Application, insertApplicationSchema } from "@shared/schema";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { Plus, Filter } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { getUserFriendlyError, userMessages } from "@/lib/user-friendly-messages";

export default function Applications() {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAppId, setSelectedAppId] = useState<number | null>(null);
  const [selectedAppName, setSelectedAppName] = useState<string>("");
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Filter state
  const [filters, setFilters] = useState({
    status: "all",
    category: "all",
    vendors: [] as string[],
  });

  // Get all applications
  const { data: applications = [] } = useQuery<Application[]>({
    queryKey: ['/api/applications'],
  });

  // Delete application mutation
  const deleteMutation = useMutation({
    mutationFn: async ({ id, forceDelete }: { id: number; forceDelete?: boolean }) => {
      const queryParams = forceDelete ? '?force=true' : '';
      return apiRequest(`/api/applications/${id}${queryParams}`, {
        method: "DELETE",
      });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['/api/applications'] });
      queryClient.invalidateQueries({ queryKey: ['/api/licenses'] });
      
      // Show appropriate success message
      let title = "Application deleted";
      let description = "The application has been deleted successfully.";
      
      if (data && data.licensesTransferred && data.licensesTransferred > 0) {
        title = "Application deleted & licenses transferred";
        description = `The application was deleted and ${data.licensesTransferred} license${data.licensesTransferred !== 1 ? 's' : ''} were moved to "Uncategorized".`;
      }
      
      toast({
        title,
        description,
      });
      
      // Close dialog and reset state
      setDeleteDialogOpen(false);
      setSelectedAppId(null);
      setSelectedAppName("");
    },
    onError: (error: any) => {
      const errorMessage = getUserFriendlyError(error);
      toast({
        title: errorMessage.title,
        description: errorMessage.description,
        variant: errorMessage.variant,
      });
    },
  });

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div className="mb-4 md:mb-0">
            <h2 className="text-2xl font-bold text-slate-800">Applications</h2>
            <p className="text-sm text-slate-500 mt-1">Manage your software applications inventory</p>
          </div>
          <div className="btn-group-mobile">
            <Button 
              variant="outline" 
              size="sm"
              className={`btn-mobile ${filters.status !== "all" || filters.category !== "all" || filters.vendors.length > 0 ? "border-blue-500 text-blue-600" : ""}`}
              onClick={() => setIsFilterDialogOpen(true)}
            >
              <Filter className="h-4 w-4 mr-2" />
              Filter
              {(filters.status !== "all" || filters.category !== "all" || filters.vendors.length > 0) && (
                <span className="ml-1 bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                  {(filters.status !== "all" ? 1 : 0) + 
                   (filters.category !== "all" ? 1 : 0) + 
                   (filters.vendors.length > 0 ? 1 : 0)}
                </span>
              )}
            </Button>
            
            <Button size="sm" onClick={() => setIsAddDialogOpen(true)} className="btn-mobile">
              <Plus className="h-4 w-4 mr-2" />
              Add Application
            </Button>
          </div>
        </div>
      </div>
      
      {/* Active Filters Display */}
      {(filters.status !== "all" || filters.category !== "all" || filters.vendors.length > 0) && (
        <div className="mb-4 bg-slate-50 p-3 rounded-md border">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium">Active Filters:</h3>
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-7 text-xs"
              onClick={() => setFilters({
                status: "all",
                category: "all",
                vendors: []
              })}
            >
              Clear All
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {filters.status !== "all" && (
              <Badge className="px-3 py-1">
                Status: {filters.status.charAt(0).toUpperCase() + filters.status.slice(1)}
              </Badge>
            )}
            {filters.category !== "all" && (
              <Badge className="px-3 py-1">
                Category: {filters.category.charAt(0).toUpperCase() + filters.category.slice(1)}
              </Badge>
            )}
            {filters.vendors.map(vendor => (
              <Badge key={vendor} className="px-3 py-1">
                Vendor: {vendor}
              </Badge>
            ))}
          </div>
        </div>
      )}
      
      {/* Application Table */}
      <ApplicationTable 
        onAddApplication={() => setIsAddDialogOpen(true)} 
        filters={filters}
        onDeleteApplication={(id: number, name: string) => {
          setSelectedAppId(id);
          setSelectedAppName(name);
          setDeleteDialogOpen(true);
        }}
      />
      
      {/* Add Application Dialog */}
      <AddApplicationDialog 
        open={isAddDialogOpen} 
        onOpenChange={setIsAddDialogOpen}
      />
      
      {/* Filter Dialog */}
      <ApplicationFilterDialog
        open={isFilterDialogOpen}
        onOpenChange={setIsFilterDialogOpen}
        onFilterChange={setFilters}
        currentFilters={filters}
        applications={applications}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        isOpen={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={(forceDelete = false) => {
          if (selectedAppId !== null) {
            deleteMutation.mutate({ id: selectedAppId, forceDelete });
          }
        }}
        applicationId={selectedAppId}
        applicationName={selectedAppName}
        isDeleting={deleteMutation.isPending}
      />

    </div>
  );
}
